/**
 * In-App Authorization Form Component
 *
 * HIPAA-compliant authorization form for when the patient is physically present
 * with the caregiver and signs on the caregiver's device.
 *
 * Implements all 8 HIPAA-required elements:
 * 1. Patient Identity
 * 2. Authorized Recipients
 * 3. Scope of Information
 * 4. Purpose of Disclosure
 * 5. Expiration
 * 6. Right to Revoke
 * 7. Redisclosure Notice
 * 8. Electronic Signature (typed name, drawn, or click-to-sign)
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { auth } from '../../lib/firebase';
import { buildHttpHeaders } from '../../utils/appCheckToken';
import { getCloudFunctionsBaseUrl } from '../../utils/cloudFunctionUrl';
import {
  Shield,
  User,
  FileText,
  Calendar,
  AlertTriangle,
  Check,
  Loader2,
  PenTool,
  Type,
  MousePointerClick,
  Trash2,
  Info,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

interface InAppAuthorizationFormProps {
  caseFileId: string;
  caseFileName: string;
  careRecipientName?: string;
  careRecipientDob?: string;
  careRecipientEmail?: string;
  onComplete: () => void;
  onCancel: () => void;
}

type SignatureMethod = 'typed_name' | 'drawn' | 'click_to_sign';
type ExpirationType = 'until_revoked' | 'date' | 'event';

// ============================================================================
// Constants
// ============================================================================

const REVOCATION_STATEMENT =
  'You have the right to revoke this authorization at any time by providing ' +
  'written notice to the authorized recipient(s). Revocation will not affect ' +
  'any actions taken in reliance on this authorization before it was revoked. ' +
  'To revoke, contact the authorized recipient or use the Hesvara platform.';

const REDISCLOSURE_NOTICE =
  'Information disclosed pursuant to this authorization may be subject to ' +
  'redisclosure by the recipient and may no longer be protected by federal ' +
  'HIPAA privacy regulations. The authorized recipient agrees not to further ' +
  'disclose your information without additional written authorization from you, ' +
  'except as required by law.';

const DEFAULT_PURPOSE =
  'Care coordination and administrative caregiving support, including but not ' +
  'limited to: managing medical records, coordinating appointments, tracking ' +
  'insurance claims, and advocating on behalf of the care recipient.';

const DEFAULT_SCOPE =
  'All documents, care logs, and notes stored in this CaseFile, including ' +
  'medical records, therapy notes, educational documents, insurance documents, ' +
  'and care coordination information.';

const AGREEMENT_TEXT =
  'By providing my electronic signature, I acknowledge that I have read, ' +
  'understand, and agree to the terms of this authorization. I understand ' +
  'that this electronic signature has the same legal effect as a handwritten ' +
  'signature under the ESIGN Act and UETA.';


// ============================================================================
// Signature Canvas Component
// ============================================================================

function SignatureCanvas({
  onSignatureChange,
  disabled,
}: {
  onSignatureChange: (dataUrl: string | null) => void;
  disabled: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  const getPos = useCallback(
    (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if ('touches' in e) {
        const touch = e.touches[0];
        return {
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
        };
      }
      return {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
    },
    []
  );

  const startDraw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled) return;
      e.preventDefault();
      isDrawingRef.current = true;
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      const pos = getPos(e);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    },
    [disabled, getPos]
  );

  const draw = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawingRef.current || disabled) return;
      e.preventDefault();
      const ctx = canvasRef.current?.getContext('2d');
      if (!ctx) return;
      const pos = getPos(e);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = '#1F3A2E';
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    },
    [disabled, getPos]
  );

  const endDraw = useCallback(() => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      onSignatureChange(canvas.toDataURL('image/png'));
    }
  }, [onSignatureChange]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      onSignatureChange(null);
    }
  }, [onSignatureChange]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = 400;
      canvas.height = 150;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, []);

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="w-full border border-soft-neutral rounded-lg cursor-crosshair touch-none bg-white"
        style={{ maxWidth: '400px', height: '150px' }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <button
        type="button"
        onClick={clear}
        disabled={disabled}
        className="btn-ghost btn-sm mt-2 flex items-center gap-1 text-muted"
      >
        <Trash2 className="w-3 h-3" />
        Clear
      </button>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function InAppAuthorizationForm({
  caseFileId,
  caseFileName,
  careRecipientName,
  careRecipientDob,
  careRecipientEmail,
  onComplete,
  onCancel,
}: InAppAuthorizationFormProps): JSX.Element {
  // Form state
  const [patientName, setPatientName] = useState(careRecipientName || '');
  const [patientDob, setPatientDob] = useState(careRecipientDob || '');
  const [patientEmail, setPatientEmail] = useState(careRecipientEmail || '');

  const [scopeDescription, setScopeDescription] = useState(DEFAULT_SCOPE);
  const [purpose, setPurpose] = useState(DEFAULT_PURPOSE);

  const [expirationType, setExpirationType] = useState<ExpirationType>('until_revoked');
  const [expirationDate, setExpirationDate] = useState('');
  const [expirationEvent, setExpirationEvent] = useState('');

  const [revocationAcknowledged, setRevocationAcknowledged] = useState(false);
  const [redisclosureAcknowledged, setRedisclosureAcknowledged] = useState(false);

  const [signatureMethod, setSignatureMethod] = useState<SignatureMethod>('typed_name');
  const [typedName, setTypedName] = useState('');
  const [drawnSignature, setDrawnSignature] = useState<string | null>(null);
  const [consentToEsign, setConsentToEsign] = useState(false);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  // Get current user info for authorized recipient
  const currentUser = auth.currentUser;
  const caregiverName = currentUser?.displayName || currentUser?.email || 'Caregiver';

  const steps = [
    'Patient Information',
    'Scope & Purpose',
    'Expiration & Rights',
    'Electronic Signature',
  ];

  // Validation
  const isStep0Valid = patientName.trim().length > 0 && patientDob.length > 0;
  const isStep1Valid = scopeDescription.trim().length > 0 && purpose.trim().length > 0;
  const isStep2Valid = revocationAcknowledged && redisclosureAcknowledged;
  const isStep3Valid = (() => {
    if (!consentToEsign) return false;
    if (signatureMethod === 'typed_name') {
      return typedName.trim().toLowerCase() === patientName.trim().toLowerCase();
    }
    if (signatureMethod === 'drawn') return drawnSignature !== null;
    if (signatureMethod === 'click_to_sign') return true;
    return false;
  })();

  const stepValid = [isStep0Valid, isStep1Valid, isStep2Valid, isStep3Valid];

  const canSubmit = stepValid.every(Boolean) && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError(null);
    setIsSubmitting(true);

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      let signatureData: string | undefined;
      if (signatureMethod === 'typed_name') {
        signatureData = typedName.trim();
      } else if (signatureMethod === 'drawn') {
        signatureData = drawnSignature || undefined;
      }

      const formData = {
        patient: {
          fullName: patientName.trim(),
          dateOfBirth: patientDob,
          ...(patientEmail.trim() && { email: patientEmail.trim() }),
        },
        authorizedRecipients: [
          {
            name: caregiverName,
            relationship: 'other',
            role: 'owner',
          },
        ],
        scope: {
          description: scopeDescription.trim(),
          caseFileId,
          caseFileName,
        },
        purpose: purpose.trim(),
        expiration: {
          type: expirationType,
          ...(expirationType === 'date' && { date: expirationDate }),
          ...(expirationType === 'event' && { event: expirationEvent.trim() }),
        },
        revocationStatement: REVOCATION_STATEMENT,
        redisclosureNotice: REDISCLOSURE_NOTICE,
        eSignature: {
          signatureMethod,
          signatureData,
          consentToEsign: true,
          agreementText: AGREEMENT_TEXT,
          signedAt: new Date().toISOString(),
          ipAddress: '', // Server will capture
          userAgent: navigator.userAgent,
          deviceInfo: `${navigator.platform} - ${navigator.userAgent.substring(0, 100)}`,
        },
        capturedByUserId: currentUser?.uid || '',
        capturedByName: caregiverName,
      };

      const baseUrl = getCloudFunctionsBaseUrl();
      const headers = await buildHttpHeaders(token);
      const response = await fetch(`${baseUrl}/processInAppAuthorizationHttp`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ caseFileId, formData }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));

        if (response.status === 429) {
          const retryAfter = errorData.retryAfterSeconds || 'a few';
          throw new Error(`Please wait ${retryAfter} seconds before trying again.`);
        }

        throw new Error(errorData.error || `Request failed with status ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        onComplete();
      } else {
        throw new Error(result.error || 'Authorization failed');
      }
    } catch (err) {
      console.error('Error submitting in-app authorization:', err);
      setError(err instanceof Error ? err.message : 'Failed to submit authorization');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-evergreen/10 rounded-full flex items-center justify-center mx-auto mb-3">
          <Shield className="w-6 h-6 text-evergreen" />
        </div>
        <h2 className="text-xl font-display text-deep-forest mb-1">
          Patient Authorization Form
        </h2>
        <p className="text-sm text-muted">
          The patient must review and sign this form in person
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {steps.map((step, i) => (
          <div key={step} className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (i <= currentStep || stepValid.slice(0, i).every(Boolean)) {
                  setCurrentStep(i);
                }
              }}
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                i === currentStep
                  ? 'bg-evergreen text-white'
                  : stepValid[i]
                    ? 'bg-evergreen/20 text-evergreen'
                    : 'bg-soft-neutral text-muted'
              }`}
            >
              {stepValid[i] ? <Check className="w-4 h-4" /> : i + 1}
            </button>
            {i < steps.length - 1 && (
              <div className={`w-8 h-0.5 ${i < currentStep ? 'bg-evergreen' : 'bg-soft-neutral'}`} />
            )}
          </div>
        ))}
      </div>
      <p className="text-center text-sm font-medium text-ink mb-4">{steps[currentStep]}</p>

      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Step 0: Patient Information */}
      {currentStep === 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-ink flex items-center gap-2">
              <User className="w-5 h-5 text-evergreen" />
              Patient Information
            </h3>
            <p className="text-sm text-muted mt-1">
              HIPAA Element 1: Who is authorizing disclosure
            </p>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label htmlFor="patientName" className="label">
                Full Legal Name <span className="text-red-500">*</span>
              </label>
              <input
                id="patientName"
                type="text"
                value={patientName}
                onChange={(e) => setPatientName(e.target.value)}
                className="input"
                placeholder="Enter patient's full legal name"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="patientDob" className="label">
                Date of Birth <span className="text-red-500">*</span>
              </label>
              <input
                id="patientDob"
                type="date"
                value={patientDob}
                onChange={(e) => setPatientDob(e.target.value)}
                className="input"
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label htmlFor="patientEmail" className="label">
                Email Address <span className="text-muted">(optional)</span>
              </label>
              <input
                id="patientEmail"
                type="email"
                value={patientEmail}
                onChange={(e) => setPatientEmail(e.target.value)}
                className="input"
                placeholder="patient@example.com"
                disabled={isSubmitting}
              />
            </div>

            <div className="bg-airy rounded-lg p-4">
              <h4 className="text-sm font-semibold text-muted mb-2">
                HIPAA Element 2: Authorized Recipients
              </h4>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brass/10 rounded-full flex items-center justify-center">
                  <User className="w-4 h-4 text-brass" />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink">{caregiverName}</p>
                  <p className="text-xs text-muted">Designated Agent (CaseFile Owner)</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Scope & Purpose */}
      {currentStep === 1 && (
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-ink flex items-center gap-2">
              <FileText className="w-5 h-5 text-evergreen" />
              Scope & Purpose
            </h3>
          </div>
          <div className="card-body space-y-4">
            <div>
              <label htmlFor="scopeDesc" className="label">
                HIPAA Element 3: Scope of Information <span className="text-red-500">*</span>
              </label>
              <textarea
                id="scopeDesc"
                value={scopeDescription}
                onChange={(e) => setScopeDescription(e.target.value)}
                className="input min-h-[100px]"
                rows={4}
                disabled={isSubmitting}
              />
              <p className="helper-text">Describes what information will be accessible</p>
            </div>
            <div>
              <label htmlFor="purpose" className="label">
                HIPAA Element 4: Purpose of Disclosure <span className="text-red-500">*</span>
              </label>
              <textarea
                id="purpose"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                className="input min-h-[80px]"
                rows={3}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Expiration & Rights */}
      {currentStep === 2 && (
        <div className="space-y-4">
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-ink flex items-center gap-2">
                <Calendar className="w-5 h-5 text-evergreen" />
                HIPAA Element 5: Expiration
              </h3>
            </div>
            <div className="card-body space-y-3">
              <label className="flex items-center gap-3 p-3 rounded-lg hover:bg-airy cursor-pointer">
                <input
                  type="radio"
                  name="expiration"
                  checked={expirationType === 'until_revoked'}
                  onChange={() => setExpirationType('until_revoked')}
                  className="h-4 w-4 text-evergreen focus:ring-evergreen"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-ink">Valid until revoked by the patient</span>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-airy cursor-pointer">
                <input
                  type="radio"
                  name="expiration"
                  checked={expirationType === 'date'}
                  onChange={() => setExpirationType('date')}
                  className="h-4 w-4 mt-0.5 text-evergreen focus:ring-evergreen"
                  disabled={isSubmitting}
                />
                <div className="flex-1">
                  <span className="text-sm text-ink">Expires on a specific date</span>
                  {expirationType === 'date' && (
                    <input
                      type="date"
                      value={expirationDate}
                      onChange={(e) => setExpirationDate(e.target.value)}
                      className="input mt-2"
                      min={new Date().toISOString().split('T')[0]}
                      disabled={isSubmitting}
                    />
                  )}
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-airy cursor-pointer">
                <input
                  type="radio"
                  name="expiration"
                  checked={expirationType === 'event'}
                  onChange={() => setExpirationType('event')}
                  className="h-4 w-4 mt-0.5 text-evergreen focus:ring-evergreen"
                  disabled={isSubmitting}
                />
                <div className="flex-1">
                  <span className="text-sm text-ink">Expires upon an event</span>
                  {expirationType === 'event' && (
                    <input
                      type="text"
                      value={expirationEvent}
                      onChange={(e) => setExpirationEvent(e.target.value)}
                      className="input mt-2"
                      placeholder="e.g., Completion of treatment"
                      disabled={isSubmitting}
                    />
                  )}
                </div>
              </label>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-ink">HIPAA Element 6: Right to Revoke</h3>
            </div>
            <div className="card-body space-y-3">
              <p className="text-sm text-ink">{REVOCATION_STATEMENT}</p>
              <label className="flex items-start gap-3 bg-airy rounded-lg p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={revocationAcknowledged}
                  onChange={(e) => setRevocationAcknowledged(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-ink">
                  I understand my right to revoke this authorization at any time <span className="text-red-500">*</span>
                </span>
              </label>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-ink">HIPAA Element 7: Redisclosure Notice</h3>
            </div>
            <div className="card-body space-y-3">
              <p className="text-sm text-ink">{REDISCLOSURE_NOTICE}</p>
              <label className="flex items-start gap-3 bg-airy rounded-lg p-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={redisclosureAcknowledged}
                  onChange={(e) => setRedisclosureAcknowledged(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                  disabled={isSubmitting}
                />
                <span className="text-sm text-ink">
                  I understand the redisclosure notice above <span className="text-red-500">*</span>
                </span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: E-Signature */}
      {currentStep === 3 && (
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-ink flex items-center gap-2">
              <PenTool className="w-5 h-5 text-evergreen" />
              HIPAA Element 8: Electronic Signature
            </h3>
            <p className="text-sm text-muted mt-1">
              Choose your preferred signature method
            </p>
          </div>
          <div className="card-body space-y-4">
            {/* Signature Method Tabs */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSignatureMethod('typed_name')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  signatureMethod === 'typed_name'
                    ? 'bg-evergreen text-white'
                    : 'bg-airy text-muted hover:text-ink'
                }`}
                disabled={isSubmitting}
              >
                <Type className="w-4 h-4" />
                Type Name
              </button>
              <button
                type="button"
                onClick={() => setSignatureMethod('drawn')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  signatureMethod === 'drawn'
                    ? 'bg-evergreen text-white'
                    : 'bg-airy text-muted hover:text-ink'
                }`}
                disabled={isSubmitting}
              >
                <PenTool className="w-4 h-4" />
                Draw
              </button>
              <button
                type="button"
                onClick={() => setSignatureMethod('click_to_sign')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  signatureMethod === 'click_to_sign'
                    ? 'bg-evergreen text-white'
                    : 'bg-airy text-muted hover:text-ink'
                }`}
                disabled={isSubmitting}
              >
                <MousePointerClick className="w-4 h-4" />
                Click to Sign
              </button>
            </div>

            {/* Typed Name */}
            {signatureMethod === 'typed_name' && (
              <div>
                <label htmlFor="typedSig" className="label">
                  Type Your Full Legal Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="typedSig"
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  className={`input ${
                    typedName.trim().length > 0 &&
                    typedName.trim().toLowerCase() !== patientName.trim().toLowerCase()
                      ? 'input-error'
                      : ''
                  }`}
                  placeholder={`Type "${patientName}" to sign`}
                  disabled={isSubmitting}
                  aria-invalid={
                    typedName.trim().length > 0 &&
                    typedName.trim().toLowerCase() !== patientName.trim().toLowerCase()
                      ? true
                      : undefined
                  }
                  aria-describedby={
                    typedName.trim().length > 0 &&
                    typedName.trim().toLowerCase() !== patientName.trim().toLowerCase()
                      ? 'typedSig-error'
                      : undefined
                  }
                />
                {typedName.trim().length > 0 &&
                  typedName.trim().toLowerCase() !== patientName.trim().toLowerCase() && (
                    <p id="typedSig-error" className="error-text" role="alert">Name must match: &quot;{patientName}&quot;</p>
                  )}
                <p className="helper-text">Your typed name serves as your electronic signature</p>
              </div>
            )}

            {/* Drawn Signature */}
            {signatureMethod === 'drawn' && (
              <div>
                <p className="label">Draw Your Signature Below <span className="text-red-500">*</span></p>
                <SignatureCanvas
                  onSignatureChange={setDrawnSignature}
                  disabled={isSubmitting}
                />
                {!drawnSignature && (
                  <p className="helper-text">Use your finger or mouse to draw your signature</p>
                )}
              </div>
            )}

            {/* Click to Sign */}
            {signatureMethod === 'click_to_sign' && (
              <div className="bg-airy rounded-lg p-6 text-center">
                <Info className="w-8 h-8 text-evergreen mx-auto mb-3" />
                <p className="text-sm text-ink mb-2">
                  By checking the consent box below and clicking &quot;Submit Authorization&quot;,
                  you confirm:
                </p>
                <p className="text-sm font-medium text-deep-forest">
                  &quot;I, {patientName || '[Patient Name]'}, authorize the above disclosure.&quot;
                </p>
              </div>
            )}

            {/* Consent Checkbox */}
            <label className="flex items-start gap-3 bg-airy rounded-lg p-4 cursor-pointer">
              <input
                type="checkbox"
                checked={consentToEsign}
                onChange={(e) => setConsentToEsign(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-soft-neutral text-evergreen focus:ring-evergreen"
                disabled={isSubmitting}
              />
              <span className="text-sm text-ink">{AGREEMENT_TEXT}</span>
            </label>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between mt-6">
        <button
          type="button"
          onClick={currentStep === 0 ? onCancel : () => setCurrentStep((s) => s - 1)}
          disabled={isSubmitting}
          className="btn-secondary"
        >
          {currentStep === 0 ? 'Cancel' : 'Back'}
        </button>

        {currentStep < steps.length - 1 ? (
          <button
            type="button"
            onClick={() => setCurrentStep((s) => s + 1)}
            disabled={!stepValid[currentStep]}
            className="btn-primary flex items-center gap-2"
          >
            Next
            <Check className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn-primary flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Shield className="w-4 h-4" />
                Submit Authorization
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

export default InAppAuthorizationForm;
